/*------------------------------------------------------------------------------
   About      : "Access Groups" category tab — quick grant/revoke of CUSTOM
                 feature groups against the selected role/user

   Custom groups are created and curated on the Access Groups top-level tab
   (AccessGroupsPanel / FeatureGroupTreePanel), but the only place an admin
   could actually assign one to a role or user was its checkbox buried inside
   the full API Permissions tree, mixed in with every ungrouped leaf feature.
   This tab reuses the same grant/revoke plumbing (grantRoleFeatureGroup /
   grantUserFeatureGroup etc., see PermissionTreeNode.tsx / ApiPermissionPanel)
   scoped to just the custom-group subtree, so assigning a bundle is a couple
   of clicks instead of a tree hunt. Individual leaf features stay editable
   only in API Permissions — this tab is bundle-level only.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button, TextField, InputAdornment } from '@mui/material';
import { Search } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { selectCustomGroupsPermissionForest, RbacEntityType } from 'store/dataReducers/rbacSlice';
import { PermissionTreeNode as PermissionTreeNodeType } from 'types/apis/rbacInterfaces';
import { GroupAssignRow } from './GroupAssignRow';

const collectGroupIds = (nodes: PermissionTreeNodeType[], acc: number[] = []): number[] => {
  nodes.forEach((node) => {
    acc.push(node.id);
    collectGroupIds(node.children || [], acc);
  });
  return acc;
};

/** Keeps a group when its own name matches, or when any descendant matches. */
const filterGroups = (nodes: PermissionTreeNodeType[], term: string): PermissionTreeNodeType[] =>
  nodes
    .map((node): PermissionTreeNodeType | null => {
      const selfMatch = node.name.toLowerCase().includes(term);
      const children = filterGroups(node.children || [], term);
      if (!selfMatch && children.length === 0) return null;
      return { ...node, children };
    })
    .filter((node): node is PermissionTreeNodeType => node !== null);

const EMPTY_COLLAPSED = new Set<number>();

export interface AccessGroupAssignmentsPanelProps {
  entityType: RbacEntityType;
  entityId: number | string;
  onSaved: () => void | Promise<void>;
}

export const AccessGroupAssignmentsPanel = ({ entityType, entityId, onSaved }: AccessGroupAssignmentsPanelProps) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();

  const groups = useSelector(selectCustomGroupsPermissionForest);

  const [pending, setPending] = useState<Map<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');

  const searchTerm = search.trim().toLowerCase();
  const searchActive = searchTerm.length > 0;

  const visibleGroups = useMemo(
    () => (groups && searchActive ? filterGroups(groups, searchTerm) : groups),
    [groups, searchActive, searchTerm],
  );

  // Groups load async — collapse everything the first time they arrive, without
  // clobbering any expand/collapse the admin has since done by hand.
  const defaultCollapseApplied = useRef(false);
  useEffect(() => {
    if (defaultCollapseApplied.current || !groups) return;
    defaultCollapseApplied.current = true;
    setCollapsed(new Set(collectGroupIds(groups)));
  }, [groups]);

  const handleToggleGroup = (groupId: number, grant: boolean, serverGranted: boolean) => {
    setPending((prev) => {
      const next = new Map(prev);
      const key = `g:${groupId}`;
      if (grant === serverGranted) next.delete(key);
      else next.set(key, grant);
      return next;
    });
  };

  const handleToggleExpand = (id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDiscard = () => setPending(new Map());

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaving(true);

    const grants: number[] = [];
    const revokes: number[] = [];
    pending.forEach((grant, key) => {
      const id = Number(key.split(':')[1]);
      (grant ? grants : revokes).push(id);
    });

    const errors: string[] = [];
    const collect = (resp: { success: boolean; error?: string }) => {
      if (!resp.success && resp.error) errors.push(resp.error);
    };

    if (entityType === 'role') {
      for (const groupId of grants) collect(await RbacApis.grantRoleFeatureGroup(rc, entityId, groupId));
      for (const groupId of revokes) collect(await RbacApis.revokeRoleFeatureGroup(rc, entityId, groupId));
    } else {
      for (const groupId of grants) collect(await RbacApis.grantUserFeatureGroup(rc, entityId, groupId));
      for (const groupId of revokes) collect(await RbacApis.revokeUserFeatureGroup(rc, entityId, groupId));
    }

    if (errors.length) toast.error(`${errors.length} change(s) failed: ${errors[0]}`);
    else toast.success('Access groups updated');

    setPending(new Map());
    await onSaved();
    setSaving(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Custom access groups grantable to this {entityType} as a bundle. Create or edit group membership under the
          Access Groups tab; individual features stay in API Permissions.
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, pb: 1 }}>
        <TextField
          size="small"
          sx={{ width: 640 }}
          placeholder="Search access groups…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 1 }}>
        {!groups && <CircularProgress size={20} sx={{ m: 1 }} />}

        {groups && groups.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No custom groups yet — create one under Access Groups to bundle permissions for assignment.
          </Typography>
        )}

        {groups && groups.length > 0 && visibleGroups?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No access groups match &quot;{search.trim()}&quot;.
          </Typography>
        )}

        {visibleGroups?.map((node) => (
          <GroupAssignRow
            key={node.id}
            node={node}
            depth={0}
            pending={pending}
            collapsed={searchActive ? EMPTY_COLLAPSED : collapsed}
            onToggleExpand={handleToggleExpand}
            onToggleGroup={handleToggleGroup}
          />
        ))}
      </Box>

      {pending.size > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {pending.size} pending change{pending.size > 1 ? 's' : ''}
          </Typography>
          <Button size="small" onClick={handleDiscard} disabled={saving}>
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
